"use client";
import { AlertTriangle } from "lucide-react";
import { Button } from "./button";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n/provider";

/**
 * Operator-density error state.
 *
 * Single critical-tone alert icon, one-line title, optional monospace
 * detail (for raw error messages), optional retry button. Used in side
 * panes, modals, and as DataTable error overrides.
 *
 * The title and retry label default to the DICTIONARY, not to English string
 * literals. They used to be `"Failed to load"` / `"Try again"` written into
 * the signature, which meant every DataTable in the product rendered its
 * error state in English no matter which language the user had picked — the
 * one moment a reader most needs to understand what they are being told.
 * Callers can still override both; they simply cannot get an untranslated
 * default any more.
 */
export function ErrorState({
  title,
  detail,
  onRetry,
  retryLabel,
  size = "md",
  className,
}: {
  title?: React.ReactNode;
  /** Raw error message — rendered in mono with break-all so long messages wrap. */
  detail?: React.ReactNode;
  onRetry?: () => void;
  retryLabel?: React.ReactNode;
  size?: "sm" | "md";
  className?: string;
}) {
  const t = useT();
  const padding = size === "sm" ? "p-4" : "p-8";
  return (
    <div
      role="alert"
      className={cn("text-center space-y-2 flex flex-col items-center", padding, className)}
    >
      <div className="inline-flex items-center gap-2 text-[12.5px] text-status-critical">
        <AlertTriangle size={14} aria-hidden />
        {title ?? t("common.failedToLoad")}
      </div>
      {detail && (
        <p className="text-[11px] text-fg-muted font-mono break-all max-w-[60ch]">{detail}</p>
      )}
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry} className="mt-1">
          {retryLabel ?? t("common.tryAgain")}
        </Button>
      )}
    </div>
  );
}
